import React, { useState, useEffect } from 'react';
import { Button, TextField, Typography, Box, Paper, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../slices/authSlice';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [gradientAngle, setGradientAngle] = useState(0);

  useEffect(() => {
    document.title = "دفتر نون";

    const isMobile = window.innerWidth < 600;
    const intervalSpeed = isMobile ? 150 : 50;

    const interval = setInterval(() => {
      setGradientAngle((prev) => (prev + 0.5) % 360);
    }, intervalSpeed);

    return () => clearInterval(interval);
  }, []);

  const handleLogin = () => {
    setLoading(true);
    dispatch(loginUser({ email: username, password }))
      .unwrap()
      .then(({ is_superuser }) => navigate(is_superuser ? '/superadmin' : '/dashboard'))
      .catch(() => setLoading(false));
  };

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading) {
      handleLogin();
    }
  };

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#F8F9FF',
        p: { xs: 2, sm: 3 },
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        style={{ width: '100%', maxWidth: 940 }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* اللوحة الجانبية المتدرجة */}
          <Box
            sx={{
              flex: { md: '0 0 42%' },
              minHeight: { xs: 180, md: 'auto' },
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              p: { xs: 4, md: 5 },
              color: '#fff',
              background: `linear-gradient(${gradientAngle}deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)`,
              transition: 'background 0.3s ease-out',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 3,
                    p: 2,
                    display: 'inline-flex',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <img
                    src="/شعار دفتر نون.png"
                    alt="دفتر نون"
                    style={{
                      width: 120,
                      maxWidth: '40vw',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ color: '#fff', fontFamily: 'Cairo, Tajawal, sans-serif', mb: 1 }}
              >
                دفتر نون
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  maxWidth: 260,
                  mx: 'auto',
                  lineHeight: 1.8,
                }}
              >
                كل ما تحتاجه لإدارة مالية مدرستك في مكان واحد
              </Typography>
            </motion.div>
          </Box>

          {/* لوحة النموذج */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 3, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                color: '#1E1B4B',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                fontSize: '1.5rem',
                mb: 0.5,
              }}
            >
              تسجيل الدخول
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#6B7280',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                mb: 3,
              }}
            >
              أدخل بياناتك للوصول إلى لوحة التحكم
            </Typography>

            {/* النموذج */}
            <Box component="form" onKeyPress={handleKeyPress}>
              <TextField
                fullWidth
                label="اسم المستخدم"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                margin="dense"
                variant="outlined"
                dir="rtl"
                inputProps={{ style: { fontSize: '0.95rem' } }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                  '& label': { fontFamily: 'Cairo, Tajawal, sans-serif' },
                  '& label.Mui-focused': { color: '#4338CA' },
                }}
              />

              <TextField
                fullWidth
                label="كلمة المرور"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="dense"
                variant="outlined"
                dir="rtl"
                inputProps={{ style: { fontSize: '0.95rem' } }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                  '& label': { fontFamily: 'Cairo, Tajawal, sans-serif' },
                  '& label.Mui-focused': { color: '#4338CA' },
                }}
              />

              {auth.error && (
                <Box
                  sx={{
                    mt: 1,
                    mb: 1,
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: '#FEE2E2',
                    border: '1px solid #FECACA',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#DC2626',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  >
                    {/* Backend explains platform-level blocks (maintenance, suspended school, expired subscription) */}
                    {typeof auth.error?.detail === 'string' && /[\u0600-\u06FF]/.test(auth.error.detail)
                      ? auth.error.detail
                      : 'فشل تسجيل الدخول، تحقق من البيانات'}
                  </Typography>
                </Box>
              )}

              <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    mt: 2.5,
                    py: 1.25,
                    borderRadius: 2.5,
                    bgcolor: '#4338CA',
                    boxShadow: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'none',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  }}
                  onClick={handleLogin}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'دخول'}
                </Button>
              </motion.div>
            </Box>

            {/* النص السفلي */}
            <Typography
              variant="body2"
              sx={{
                color: '#6B7280',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                fontSize: '0.8rem',
                textAlign: 'center',
                mt: 3,
              }}
            >
              دفتر نون — كل ما تحتاجه لإدارة مالية مدرستك في مكان واحد
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default LoginPage;
